import { useCallback, useEffect, useMemo, useRef } from 'react';

import { fetchDeposits } from '#/api/apiExchange';
import { useInvalidateAllVaultsQueries } from '#/api/queries/vaultsQueries';
import {
  useActiveWindowInterval,
  useWebsocketEventListener,
} from '#/components/hooks';
import config from '#/config';
import {
  DEPOSIT_MINED_FETCH_DELAY_MS,
  EXCHANGE_TRANSFER_BRIDGE_SOURCE_OR_TARGET,
  VAULTS_REFRESH_SETTLE_DELAY_MS,
} from '#/config/constants';
import { hoursDifferenceLongerThan } from '#/lib/date';
import { getAnonymousRPCProvider } from '#/lib/ethereum/wallets/utils';
import { waitAsync } from '#/lib/promiseTools';
import {
  updatePendingDepositConfirmations,
  updateBridgedPendingDeposit,
  removePendingDeposit,
} from '#/reduxState/balances/pendingDeposits';
import {
  isLoopbackPendingDepositMatch,
  isLoopbackQuantityApproxMatch,
  notifyAboutCompletedDeposit,
  notifyAndUpdatePendingDepositStatusToFailed,
  useSelectPendingDepositsListIncludingFailed,
} from '#/reduxState/balances/pendingDepositsUtils';
import { PendingDepositItem } from '#/reduxState/balances/pendingDepositTypes';
import { useSelector, staticDispatch } from '#/reduxState/store';

import { MIN_DEPOSIT_QUANTITY_MULTIPLIER } from '../bridge/stargate/stargateConstants';
import { getStargateSwapStatus } from '../bridge/stargate/stargateStatus';

const blockConfirmationDelay = config.chains.katanaChain.blocksConfirmation;

/** Loopback deposits are normally credited within a few seconds */
const PENDING_LOOPBACK_MAX_AGE_MS = 5 * 60 * 1000;
const PENDING_VAULT_DEPOSIT_MAINTENANCE_INTERVAL_MS = 5_000;

/**
 * Deposit overlay is responsible for displaying modal on transactions being successuflly mined, or rejected by network.
 *
 * This coomponent should be always mount on top level,
 * so it can fetch pending transaction information.
 */
function DepositBackgroundWorker() {
  const invalidateAllVaultsQueries = useInvalidateAllVaultsQueries();
  const walletAddress = useSelector(state => state.wallet.walletAddress);
  const pendingDepositsList = useSelectPendingDepositsListIncludingFailed();
  const pendingConfirmationsCount = useSelector(
    state => state.balances.pendingDeposits.confirmationsCounts
  );
  const vaultDepositFee = useSelector(
    state => state.exchange.info.vaultDepositFee
  );

  /** Check pending deposits against deposits persisted in the API */
  const checkPendingDepositsInApi = useCallback(async () => {
    if (!walletAddress || pendingDepositsList.length <= 0) {
      return;
    }
    try {
      const persistedDepositsApi = await fetchDeposits({
        wallet: walletAddress,
        limit: 10,
      });
      for (const pendingDeposit of pendingDepositsList) {
        const sourceTx = pendingDeposit.sourceTxId?.toLowerCase();
        const bridgeTx = pendingDeposit.bridgeTxId?.toLowerCase();
        const completedDeposit = persistedDepositsApi.find(depositInApi => {
          if (pendingDeposit.loopback) {
            // No on-chain source TX for loopback deposits - approximate match
            return isLoopbackPendingDepositMatch(pendingDeposit, depositInApi);
          }
          const apiTx = // Special handling to narrow types for non exchange transfers
            depositInApi.bridgeSource ===
            EXCHANGE_TRANSFER_BRIDGE_SOURCE_OR_TARGET
              ? depositInApi.katanaTxId?.toLowerCase()
              : depositInApi.bridgeTxId?.toLowerCase() ||
                depositInApi.forwarderTxId?.toLowerCase() ||
                depositInApi.katanaTxId?.toLowerCase();
          return bridgeTx === apiTx || sourceTx === apiTx;
        });
        const txConfirmedInApi = sourceTx && completedDeposit;
        if (txConfirmedInApi) {
          console.log('[deposit] Confirmed in the API');
          // User could miss WebSocket notification (eg. opening web later)
          const creditedEarlier = Date.now() > completedDeposit.time + 20_000; // 20 seconds
          // Loopback deposits complete silently (no notification)
          if (creditedEarlier && !pendingDeposit.loopback) {
            notifyAboutCompletedDeposit(completedDeposit.quantity);
          }
          if (pendingDeposit.vault || pendingDeposit.loopback) {
            // Refetch vault data (tables, balances) first and only then
            // remove the pending row, so the credited deposit is already
            // visible in the tables when the pending row disappears. The
            // removal must run even when the refetch fails.
            await invalidateAllVaultsQueries().catch(console.warn);
          }
          staticDispatch(removePendingDeposit(pendingDeposit.sourceTxId));
        }
      }
    } catch (error) {
      console.warn(error);
    }
  }, [invalidateAllVaultsQueries, pendingDepositsList, walletAddress]);

  // Check confirmed in the API (on page load / new pending deposit)
  useEffect(() => {
    checkPendingDepositsInApi();
  }, [checkPendingDepositsInApi]);

  const hasPendingVaultDeposits = useMemo(
    () =>
      pendingDepositsList.some(deposit => deposit.vault || deposit.loopback),
    [pendingDepositsList]
  );

  // Once all pending vault deposits complete (or are cleared) refresh vault
  // data via the effect cleanup - immediately and once more after the settle
  // delay, as refetching at the crediting moment can still race the chain
  // state / indexers
  useEffect(() => {
    if (!hasPendingVaultDeposits) {
      return;
    }
    return () => {
      invalidateAllVaultsQueries();
      waitAsync(VAULTS_REFRESH_SETTLE_DELAY_MS).then(
        invalidateAllVaultsQueries
      );
    };
  }, [hasPendingVaultDeposits, invalidateAllVaultsQueries]);

  // Vault deposit websocket events are only delivered to the vault manager
  // wallet - a non-manager depositor gets no event, so re-check the API on a
  // short interval while a vault deposit is pending (loopback deposits
  // normally credit within seconds)
  useActiveWindowInterval(() => {
    // A pending loopback that never matched (eg. charged fees outside the
    // match tolerance) would stay pending forever - clear it silently
    pendingDepositsList
      .filter(
        deposit =>
          deposit.loopback &&
          Date.now() - deposit.time > PENDING_LOOPBACK_MAX_AGE_MS
      )
      .forEach(deposit => {
        console.error(
          '[deposit] Clearing unmatched loopback deposit',
          deposit.sourceTxId
        );
        staticDispatch(removePendingDeposit(deposit.sourceTxId));
      });

    if (hasPendingVaultDeposits) {
      checkPendingDepositsInApi();
    }
  }, PENDING_VAULT_DEPOSIT_MAINTENANCE_INTERVAL_MS);

  /** Deposits for check confirmations */
  const pendingDepositsWithNonMaxConfirmations = useMemo(
    () =>
      blockConfirmationDelay
        ? pendingDepositsList.filter(deposit => {
            if (deposit.loopback) {
              // Loopback deposits have no on-chain TX to check confirmations for
              return false;
            }
            const confirmations = pendingConfirmationsCount[deposit.sourceTxId];
            if (confirmations === -1) {
              return false;
            }
            const bridgedConfirmations =
              pendingConfirmationsCount[deposit.bridgeTxId || '-'];
            if (bridgedConfirmations === -1) {
              return false;
            }
            const requiredConfirmations = deposit.bridge
              ? 1 // For initial bridge TX, we only need single confirmation
              : blockConfirmationDelay;
            return deposit.bridge && deposit.bridgeTxId
              ? (bridgedConfirmations || 0) < blockConfirmationDelay
              : (confirmations || 0) < requiredConfirmations;
          })
        : [],
    [pendingConfirmationsCount, pendingDepositsList]
  );

  const pendingCrossChainWithoutBridgedTx = useMemo(
    () =>
      blockConfirmationDelay
        ? pendingDepositsList.filter(
            deposit =>
              deposit.bridge &&
              // Only those which are already mined initial TX
              pendingConfirmationsCount[deposit.sourceTxId] &&
              !deposit.bridgeTxId
          )
        : [],
    [pendingConfirmationsCount, pendingDepositsList]
  );

  const bridgedCheckAsync = useCallback(
    async (deposit: PendingDepositItem) => {
      if (
        pendingConfirmationsCount[deposit.sourceTxId] &&
        deposit.bridge === 'Stargate'
      ) {
        const stargateStatus = await getStargateSwapStatus(
          deposit.sourceTxId,
          deposit.forwarderTxId
        );
        if (
          stargateStatus?.destinationTxHash ||
          stargateStatus?.forwarderLzComposeTxHash
        ) {
          staticDispatch(
            updateBridgedPendingDeposit(
              {
                txHash: deposit.sourceTxId,
                forwarderTxId: stargateStatus.forwarderLzComposeTxHash,
                bridgeTxId: stargateStatus.destinationTxHash,
              }
              // stargateStatus.message.srcUaNonce,
            )
          );
        }
        if (stargateStatus?.status === 'FAILED') {
          staticDispatch(
            notifyAndUpdatePendingDepositStatusToFailed(deposit.sourceTxId)
          );
        }
      }
    },
    [pendingConfirmationsCount]
  );

  /** Prevent multiple checks running in parallel, if network is slow */
  const depositCheckPending = useRef(false);

  useActiveWindowInterval(
    async function depositsWorker() {
      if (!walletAddress || depositCheckPending.current) {
        return;
      }
      depositCheckPending.current = true;
      const depositsToCheck = pendingDepositsWithNonMaxConfirmations.filter(
        deposit => {
          const isOld = hoursDifferenceLongerThan(
            deposit.time,
            Date.now(),
            24 * 5 /* 5 days */
          );
          // Stop doing realtime check if deposit is stuck.
          // There will be done API check once user reload page.
          if (isOld) {
            console.warn(
              `Skipping check - Stuck deposit ${deposit.sourceTxId}`
            );
          }
          return !isOld;
        }
      );
      for (const deposit of depositsToCheck) {
        const txHashToCheck = deposit.bridgeTxId
          ? deposit.bridgeTxId
          : deposit.sourceTxId;

        // Update not confirmed first by blockchain (Until we have at least one confirmation)
        await getAnonymousRPCProvider(
          config.chains[
            deposit.bridgeTxId ? 'katanaChain' : deposit.sourceChain
          ]
        )
          // Warn: this is doing 4 RPC requests
          .getTransactionReceipt(txHashToCheck)
          .then(async receipt => {
            if (!receipt) {
              return;
            }
            console.log('TX status:', receipt);
            const confirmed: number =
              !receipt || receipt.status === null
                ? 0 // Not expected
                : receipt.status <= 0 // Post-byzantium blocks will have a status (0 = failure)
                ? -1
                : await receipt.confirmations();
            console.log('Confirmed: ', txHashToCheck, confirmed);
            staticDispatch(
              confirmed === -1
                ? notifyAndUpdatePendingDepositStatusToFailed(txHashToCheck)
                : updatePendingDepositConfirmations(txHashToCheck, confirmed)
            );
            if (confirmed && deposit.bridge && !deposit.bridgeTxId) {
              return bridgedCheckAsync(deposit);
            }
          })
          .catch(error => {
            console.warn(error);
          });
      }

      for (const deposit of pendingCrossChainWithoutBridgedTx) {
        await bridgedCheckAsync(deposit);
      }

      depositCheckPending.current = false;
    },
    15 * 1000 * (pendingDepositsWithNonMaxConfirmations.length || 1)
  );

  useWebsocketEventListener('deposits', async depositWs => {
    const receivedQuantity = Number(depositWs.data.quantity);
    const possibleDepositsList = pendingDepositsList.filter(deposit => {
      if (deposit.loopback) {
        return isLoopbackQuantityApproxMatch(deposit, receivedQuantity);
      }
      const sourceQuantity = Number(deposit.quantity);
      const minQuantity =
        sourceQuantity * MIN_DEPOSIT_QUANTITY_MULTIPLIER -
        (deposit.vault ? Number(vaultDepositFee || 0) : 0);
      return (
        receivedQuantity >= minQuantity && receivedQuantity <= sourceQuantity
      );
    });
    if (possibleDepositsList.length <= 0) {
      return;
    }
    await waitAsync(DEPOSIT_MINED_FETCH_DELAY_MS);
    try {
      const persistedDeposits = await fetchDeposits({
        wallet: walletAddress,
        limit: 4,
      });
      for (const deposit of persistedDeposits) {
        const foundPending = possibleDepositsList.find(pendingDeposit =>
          pendingDeposit.loopback
            ? isLoopbackPendingDepositMatch(pendingDeposit, deposit)
            : deposit.bridgeTxId
            ? pendingDeposit.bridgeTxId === deposit.bridgeTxId
            : pendingDeposit.sourceTxId === deposit.katanaTxId
        );
        if (foundPending) {
          if (foundPending.vault || foundPending.loopback) {
            // Refetch vault data first and only then remove the pending row,
            // so the credited deposit is already visible in the tables. The
            // removal must run even when the refetch fails.
            await invalidateAllVaultsQueries().catch(console.warn);
          }
          // Notification already displayed by notificationSdk (WebSocket)
          staticDispatch(removePendingDeposit(foundPending.sourceTxId));
        }
      }
    } catch (error) {
      console.warn(error);
    }
  });

  return null;
}

export default DepositBackgroundWorker;
